import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, getRemotedbUrl, isNotEmpty } from '../../lib/ep.js';

import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection';
import { DeleteButton } from '../builder/ConfirmButton';
import DisabledArea from '../builder/DisabledArea';
import ImageUploader from '../builder/ImageUploader';
import Input from '../builder/Input';
import Model from '../builder/Model';
import SaveButton from '../builder/SaveButton';
import SectionTitle from '../builder/SectionTitle';
import SectionSubtitle from '../builder/SectionSubtitle';
import Select from '../builder/Select';
import RichEditor from '../builder/RichEditor';
import TextArea from '../builder/TextArea';
import Tooltip from '../builder/Tooltip.js';

const ConvertToTemplateOptions = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? false;

	const organizers = useRef([]);
	if(props.organizers.length > 0) {
		props.organizers.map((ob) => {
			organizers.current.push({ k:ob.id, v:ob.name });
		});
	}

	const defaultTemplate = {
		id:null,
		title:"",
		short_description:"",
		image:"",
		bg_color:'rgba(128, 128, 128, 1)',
		labels:[],
		target:promotion.id,
		platform:promotion.promo_type,
		instructions:"",
	};

	const generateTemplateState = () => {
		return { ...defaultTemplate, ...(props?.template ?? {})};
	};

	const [ error_msg, setErrorMsg ] = useState(null);
	const [ errors, setErrors ] = useState({});
	const [ template, setTemplate ] = useState(generateTemplateState());

	const validateFields = (obj) => {
		const _error_msg = [];
		const _errors = {};

		if(obj.title == "") {
			_errors.title = "This field cannot be empty";
			_error_msg.push(i18n("You need to specify a title"));
		}

		if(obj.short_description == "") {
			_errors.short_description = "This field cannot be empty";
			_error_msg.push(i18n("You need to specify a short description"));
		}

		if(obj.labels.length < 1) {
			_errors.labels = "This field cannot be empty";
			_error_msg.push(i18n("You need to select at least one organizing brand"));
		}

		setErrors(_errors);
		setErrorMsg((_error_msg.length > 0) ? _error_msg.join("<br />") : null);
	};

	useEffect(() => {
		if(template.id != undefined) {
			validateFields({ ...template });
		}
	}, []);

	if(promotion === false || organizers.current.length == 0) return null;

	const change = (k, v) => {
		const _template = { ...template, [k]:v };
		setTemplate(_template);

		validateFields(_template);
	};

	const addOrganizingBrand = (k, v) => {
		if(v == '----') return;

		const _labels = [ ...template.labels ];
		_labels.push(v);

		const _template = { ...template, labels:_labels };
		setTemplate(_template);
		
		validateFields(_template);
	};

	const deleteOrganizingBrand = (id) => {
		const _labels = [ ...template.labels ].filter((obj) => (obj != id));

		const _template = { ...template, labels:_labels };
		setTemplate(_template);

		validateFields(_template);
	}

	const selectAllBrands = () => {
		if(errors.labels != undefined) {
			const _errors = { ...errors };
			delete _errors.labels;
			setErrors(_errors);
		}
		
		const _labels = [ ...template.labels ];
		organizers.map((organizer) => {
			if(template.labels.indexOf(organizer.id) > -1) return;
			_labels.push(organizer.id);
		});

		const _template = { ...template, labels:_labels };
		setTemplate(_template);

		validateFields(_template);
	};

	const selectNoneBrands = () => {
		const _template = { ...template, labels:[] };
		setTemplate(_template);

		validateFields(_template);
	};

	const deleteCustomTemplate = () => {
		if(template.id == undefined) return false;

		$.post(getRemotedbUrl('/agents/delete_template'), { template_id:template.id }, (res) => {
			if(res.status == '1') {
				setTemplate({});

				if(props.callback != undefined) props.callback('delete_template', false);
			}
		}, 'JSON');
	};

	const createTemplate = (cb) => {
		$.post(getRemotedbUrl('/agents/template_create'), { template_info:template }, (res) => {
			if(res.status == '1') {
				if(cb != undefined) cb();
				if(props.callback != undefined) props.callback('create_template', template);

				setTemplate({ ...template, id:res.id });
			}
		}, 'JSON');
	};

	const labels_selector = [{ k:'----', v:`-- ${i18n("Select")} --` }];
	if(template.labels != undefined) {
		organizers.current.map((_organizer) => {
			let organizer = _organizer;
			if(organizer.id != undefined && organizer.k == undefined) {
				organizer = { k:organizer.id, v:organizer.name };
			}
			
			if(template.labels.indexOf(organizer.k) > -1) return;

			labels_selector.push(organizer);
		});
	}

	return (
		<div>
			<SectionTitle>Customize the main options of the template</SectionTitle>

			<SectionSubtitle>This will be displayed in the template selector for all the agents</SectionSubtitle>

			<Model model={template} change={change} errors={errors}>
				<Input
					name="title"
					placeholder={(isNotEmpty(promotion, 'title')) ? promotion.title : null}
				>Title</Input>

				<TextArea
					name="short_description"
					max_length={255}
				>Short description</TextArea>

				<RichEditor
					name="instructions"
					placeholder={promotion.instructions}
				>Instructions</RichEditor>

				<ImageUploader
					image={template.image}
					name="image"
					_thumb_size={{ width:280 }}
					_size={{ width:1200, height:630 }}
				>Image</ImageUploader>
			</Model>

			<DisabledArea
				disabled={(labels_selector.length > 1) ? false : true}
				message="There are no more organizing brands"
			>
				<Model model={template} change={addOrganizingBrand}>
					<Select
						name="labels"
						values={labels_selector}
						label_props={{
							_class:{ other:'gui_flexbox_auto gui_flex_wrap gui_flex_space_between without_colon' },
							_extra_component:<ActionLink
								onClick={() => selectAllBrands()}
							>Select all</ActionLink>,
							autotranslate:false,
						}}
					>{i18n("Organizing brands")}:</Select>
				</Model>
			</DisabledArea>

			{(template.labels.length > 0) && (
				<div class="ribbons gui_not_in_top_tiny" style={{zIndex:'9'}}>
					{template.labels.map((item, i) => {
						const _brand = organizers.current.find((o) => (o.k == item));

						return (
							<span key={`brand_${_brand.k}`} class="linked_ribbon">
								<ActionLink
									icon='knk-off'
									_class={{ other:'with_icon blackColor blackIcon lightgrayBg' }}
									onClick={() => deleteOrganizingBrand(_brand.k)}
									autotranslate={false}
									tooltip={<Tooltip position="top">Click here to remove</Tooltip>}
								>{_brand.v}</ActionLink>
							</span>
						)
					})}

					<span class="linked_ribbon">
						<ActionLink
							icon='knk-delete'
							_class={{ other:'with_icon blackColor redIcon lightgrayBg' }}
							onClick={() => selectNoneBrands()}
							tooltip={<Tooltip position="top">Click here to remove all the brands from the template</Tooltip>}
						>Clear selection</ActionLink>
					</span>
				</div>
			)}

			<ButtonCollection in_floating={true}>
				<SaveButton
					disabled={(Object.keys(errors).length > 0) ? true : false}
					onClick={(e, cb) => createTemplate(cb)}
					error_msg={error_msg}
				>{(template.id != null) ? "Update template" : "Create template"}</SaveButton>

				{(template.id != undefined) && (
					<DeleteButton
						onClick={() => deleteCustomTemplate()}
					>Delete template</DeleteButton>
				)}
			</ButtonCollection>
		</div>
	);
};

export default injectIntl(ConvertToTemplateOptions);